import React, {
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import styles from './Contact.module.scss';
import { Box, Skeleton } from '@mui/material';
import { ContactsContext } from '../../context/ContactsContextProvider/ContactsContextProvider';
import { ContactField, ContactFields, ContactFieldProps } from './ContactField';
import validations from '../../lib/helpers/validations';
import { NewContact } from '../../hooks/useContacts';

export type ContactFieldValidation = {
	id?: string;
	firstName?: string;
	lastName?: string;
	companyName?: string;
	customer: {
		email?: string;
	};
};

export type ContactProps = {
	contactIndex: number;
	validationEnabled: boolean;
	className?: string;
	isVisible: boolean;
	onChange?: (
		contact: ( NewContact & { id?: string } ) | undefined,
		index: number
	) => void;
} & Pick<ContactFieldProps, 'disabled' | 'optional' | 'descriptiveLabels'>;

const Contact: React.FC<ContactProps> = ( {
	contactIndex,
	validationEnabled,
	disabled,
	optional,
	className,
	descriptiveLabels,
	isVisible: propIsVisible,
	onChange,
} ) => {
	// we have to wait one render cycle to really have the anchorRef be visible again after a tab change
	const [ isVisible, setIsVisible ] = useState( propIsVisible );
	useEffect( () => {
		setIsVisible( propIsVisible );
	}, [ propIsVisible ] );
	// but we _don't_ want to wait for the next render cycle if we know it's _not_ going to be visible
	const calculatedIsVisible = useMemo(
		() => ( propIsVisible === false ? false : isVisible ),
		[ propIsVisible, isVisible ]
	);
	// A ref to position the popper relative too
	const ref = useRef<HTMLDivElement>( null );
	// Context hooks
	const contactsContext = useContext( ContactsContext );
	const contact = contactsContext?.contacts?.[ contactIndex ];
	const [ contextTriggeredValidation, setContextTriggeredValidation ] =
		useState( false );
	const [ showCompanyName, setShowCompanyName ] = useState(
		!!contact?.companyName
	);

	useEffect( () => {
		setShowCompanyName( !!contact?.companyName );
	}, [ contact?.companyName ] );

	const [ validation, setValidation ] = useState<
		ContactFieldValidation | undefined
	>();
	const validate = useCallback(
		( force?: boolean ) => {
			if ( ( !validationEnabled && !force ) || disabled ) {
				setValidation( {
					firstName: '',
					lastName: '',
					companyName: '',
					customer: { email: '' },
				} );
				return true;
			}
			// only validate the contact if one of the values is provided, or if it's required
			if (
				contact?.firstName ||
				contact?.lastName ||
				contact?.customer?.email ||
				contact?.companyName ||
				!optional
			) {
				const validation: ContactFieldValidation = {
					firstName: validations.validateName( contact?.firstName ),
					lastName: validations.validateName( contact?.lastName ),
					companyName: validations.validateCompanyName( contact?.companyName ),
					customer: {
						email: validations.validateEmail( contact?.customer?.email ),
					},
				};
				setValidation( validation );
				const isValid =
					!validation?.firstName &&
					!validation?.lastName &&
					!validation?.customer?.email &&
					!validation?.companyName;
				return isValid;
			} else {
				// If it's not invalid then it should be valid
				setValidation( {
					firstName: '',
					lastName: '',
					companyName: '',
					customer: { email: '' },
				} );
				return true;
			}
		},
		[
			contact?.customer?.email,
			contact?.firstName,
			contact?.lastName,
			contact?.companyName,
			disabled,
			optional,
			validationEnabled,
		]
	);

	/* we want to register validation on mount, but if we remove a contact from a form (like
	on contracts, where it can draw with two contacts and then refresh to one after the hook
	completes) we need to remove the validation on unmount*/
	useEffect( () => {
		contactsContext?.setValidationCallback(
			( force ) => validate( validationEnabled || force ),
			contactIndex
		);
		return () => {
			contactsContext?.setValidationCallback( undefined, contactIndex );
		};
	}, [
		contactIndex,
		contactsContext,
		validate,
		validationEnabled
	] );

	/* if validationEnabled changes from false to true we register a new callback in the
	ContactsContext above, but also immediately call it here */
	useEffect( () => {
		if ( validationEnabled ) {
			validate( validationEnabled );
		}
	}, [ validate, validationEnabled ] );

	/* Because if we select a contact suggestion that updates context but doesn't "roll down"
	to this component for a render cycle. So on next render cycle revalidate after selecting
	a suggestion */
	useEffect( () => {
		if ( contextTriggeredValidation ) {
			setContextTriggeredValidation( false );
			validate();
		}
	}, [ contextTriggeredValidation, validate ] );

	useEffect( () => {
		onChange?.( contact, contactIndex );
	}, [
		contact,
		contactIndex,
		onChange
	] );

	const set: ContactFieldProps['onChange'] = useCallback(
		( e: Parameters<NonNullable<ContactFieldProps['onChange']>>[0] ) => {
			contactsContext?.set( e, contactIndex );
			// Trigger another validation on the next render cycle after selecting a suggestion
			setContextTriggeredValidation( true );
		},
		[ contactIndex, contactsContext ]
	);

	const anchorRef = ref.current;

	return (
		<Box
			className={ [ styles.directInputFields, className ].join( ' ' ) }
			ref={ ref }
			data-cy='customer'
		>
			{ !contactsContext || contactsContext.loading || !anchorRef ? (
				<>
					<Skeleton className={ styles.skeleton } />
					<Skeleton className={ styles.skeleton } />
					<Skeleton className={ styles.skeleton } />
				</>
			) : (
				<>
					<ContactField
						contactField={ ContactFields.firstName }
						contact={ contact }
						error={ validation?.firstName }
						options={ contactsContext?.organizationContacts }
						onChange={ set }
						disabled={ disabled }
						optional={ optional }
						anchorRef={ anchorRef }
						isVisible={ calculatedIsVisible }
						onBlur={ () => validate() }
						descriptiveLabels={ descriptiveLabels }
					/>
					<ContactField
						contactField={ ContactFields.lastName }
						contact={ contact }
						error={ validation?.lastName }
						options={ contactsContext?.organizationContacts }
						onChange={ set }
						disabled={ disabled }
						optional={ optional }
						anchorRef={ anchorRef }
						isVisible={ calculatedIsVisible }
						onBlur={ () => validate() }
						descriptiveLabels={ descriptiveLabels }
					/>
					<ContactField
						contactField={ ContactFields.email }
						contact={ contact }
						error={ validation?.customer?.email }
						options={ contactsContext?.organizationContacts }
						onChange={ set }
						disabled={ disabled }
						optional={ optional }
						anchorRef={ anchorRef }
						isVisible={ calculatedIsVisible }
						onBlur={ () => validate() }
						descriptiveLabels={ descriptiveLabels }
					/>
					{ showCompanyName ? (
						<ContactField
							contactField={ ContactFields.companyName }
							contact={ contact }
							error={ validation?.companyName }
							options={ contactsContext?.organizationContacts }
							onChange={ set }
							disabled={ disabled }
							optional={ true }
							anchorRef={ anchorRef }
							isVisible={ calculatedIsVisible }
							onBlur={ () => validate() }
							className={ styles.companyNameField }
						/>
					) : disabled ? null : (
						<button
							className={ styles.addCompanyName }
							data-cy='add-company-name'
							onClick={ () => setShowCompanyName( true ) }
						>
							+ Company name (optional)
						</button>
					) }
				</>
			) }
		</Box>
	);
};

export default Contact;
